export type BusinessGuideTag = "Must Read" | "Money" | "Beginner" | "Advanced" | "Workflow";

export type BusinessGuide = {
  id: string;
  title: string;
  category: "Start" | "Pricing" | "Management" | "Marketing" | "Scaling";
  summary: string;
  tags: BusinessGuideTag[];
  content: {
    overview: string;
    whyItMatters: string;
    steps: string[];
    mistakes: string[];
    proTip: string;
    bottomLine: string;
  };
};

export const businessGuides: BusinessGuide[] = [
  // ======================
  // START
  // ======================

  {
    id: "start-dtf-business",
    title: "How to Start a DTF Business",
    category: "Start",
    summary: "Build a strong foundation without overspending, overpromising, or creating chaos early.",
    tags: ["Must Read", "Beginner"],
    content: {
      overview:
        "Starting a DTF business is not just about buying equipment and hoping orders appear. The strongest shops start with a clear offer, a realistic workflow, and simple systems that can actually be maintained when demand increases.",
      whyItMatters:
        "A weak start creates expensive problems later. Underpricing, bad customer communication, messy order intake, and poor process control can trap a business before it becomes stable.",
      steps: [
        "Decide exactly what you are selling first, such as transfers only, finished shirts, business apparel, event work, or a mix.",
        "Start with a setup you can afford to run and maintain consistently instead of chasing the biggest setup you can finance.",
        "Track your actual costs from day one, including film, ink, powder, waste, supplies, shipping materials, and your time.",
        "Create simple rules for approvals, payments, turnaround, and remake limits before you get busy.",
        "Focus on repeatable quality first. It is better to be reliable than to look huge too early."
      ],
      mistakes: [
        "Buying too much equipment before demand exists.",
        "Underpricing just to get early sales.",
        "Promising rush timelines before your workflow is stable.",
        "Running everything through random texts and DMs."
      ],
      proTip:
        "A smaller shop with clear systems usually grows faster than a messy shop with more equipment.",
      bottomLine:
        "Start with clarity, control, and consistency. Growth comes easier when the foundation is solid."
    }
  },

  {
    id: "equipment-needed",
    title: "What Equipment You Actually Need First",
    category: "Start",
    summary: "Buy what helps you produce reliably, not what makes you look the biggest.",
    tags: ["Beginner", "Money"],
    content: {
      overview:
        "Most beginners waste money because they think a bigger setup automatically means a better business. In reality, the right first equipment is the gear that lets you produce clean, repeatable work without crushing your budget.",
      whyItMatters:
        "Overspending early reduces flexibility. Money tied up in unnecessary equipment cannot be used for supplies, marketing, repairs, testing, and the real costs of learning.",
      steps: [
        "Choose a printer setup you can realistically maintain, troubleshoot, and afford to keep supplied.",
        "Buy a dependable heat press because pressing issues can ruin otherwise good production.",
        "Prioritize essential supplies such as film, powder, ink, cleaning items, storage solutions, and packaging basics.",
        "Purchase tools that improve reliability and consistency before buying flashy extras.",
        "Upgrade only after a real bottleneck proves the need."
      ],
      mistakes: [
        "Buying everything at once out of fear.",
        "Choosing gear based on hype instead of workflow.",
        "Ignoring maintenance tools until problems show up.",
        "Spending too much on appearance before building demand."
      ],
      proTip:
        "Reliable basics beat impressive clutter every time.",
      bottomLine:
        "Buy for stable production first. Let real demand decide the next upgrade."
    }
  },

  {
    id: "workspace-setup",
    title: "Setting Up a Small DTF Workspace",
    category: "Start",
    summary: "Create a shop layout that saves time, reduces mistakes, and feels easier to run.",
    tags: ["Beginner", "Workflow"],
    content: {
      overview:
        "A small workspace can still be highly effective if the layout is intentional. Good flow reduces wasted movement, protects materials, and makes production feel more controlled.",
      whyItMatters:
        "Bad setups create drag all day long. Clutter, poor storage, mixed work zones, and bad airflow add stress and increase the chance of mistakes.",
      steps: [
        "Separate your main zones as much as possible: printing, curing, pressing, finishing, and packing.",
        "Keep frequently used supplies near the part of the workflow where they are actually needed.",
        "Store film, powder, ink, garments, tools, and packaging in labeled, repeatable spots.",
        "Control temperature, humidity, and ventilation as much as possible.",
        "Design the room around movement so you are not constantly doubling back."
      ],
      mistakes: [
        "Letting the shop become reactive and cluttered.",
        "Mixing dirty work areas with clean finishing space.",
        "Ignoring airflow and environmental control.",
        "Creating storage with no system for putting things back."
      ],
      proTip:
        "Layout matters more than square footage.",
      bottomLine:
        "A clean, organized workspace makes production faster, calmer, and more professional."
    }
  },

  {
    id: "beginner-mistakes",
    title: "Biggest Beginner Mistakes in DTF",
    category: "Start",
    summary: "Avoid the common early mistakes that waste money, time, and confidence.",
    tags: ["Must Read", "Beginner"],
    content: {
      overview:
        "Most beginners struggle with the same early failures. Many of them are preventable if you slow down, test properly, and build repeatable habits instead of relying on luck.",
      whyItMatters:
        "Early mistakes do not just waste material. They damage confidence, create bad customer experiences, and make the whole process feel harder than it needs to be.",
      steps: [
        "Learn proper curing and pressing instead of guessing from one or two successful prints.",
        "Test different garments, films, and powders before treating a setup like it works on everything.",
        "Run controlled samples before taking on important jobs.",
        "Keep notes on settings, materials, and environmental conditions that produced good results.",
        "Treat maintenance as part of production, not as an emergency-only task."
      ],
      mistakes: [
        "Skipping tests and going straight into paid orders.",
        "Changing too many variables at once when troubleshooting.",
        "Guessing at cure and press settings.",
        "Ignoring maintenance until something fails."
      ],
      proTip:
        "Write down what works so you do not have to relearn it under pressure.",
      bottomLine:
        "Good habits early save money and speed up your path to consistency."
    }
  },

  {
    id: "services-offer",
    title: "Choosing What Services to Offer",
    category: "Start",
    summary: "Offer services that fit your setup, skill level, and business goals.",
    tags: ["Beginner", "Money"],
    content: {
      overview:
        "Not every DTF business should offer everything. Your best early offers are the ones you can produce consistently, explain clearly, and profit from without adding too much complexity.",
      whyItMatters:
        "Too many offers too early make sales and production messier. A focused offer is easier to sell and easier to fulfill well.",
      steps: [
        "Decide whether you want to focus on transfers, finished garments, or a mix.",
        "Pick a target customer such as local businesses, brand owners, sports teams, events, or retail buyers.",
        "Start with services that fit your current workflow instead of forcing your workflow to fit too many services.",
        "Describe your offer in simple language customers understand quickly.",
        "Expand later once demand and systems can support it."
      ],
      mistakes: [
        "Trying to offer everything immediately.",
        "Having no clear target customer.",
        "Building complicated offers that are hard to explain.",
        "Adding services before you are consistent."
      ],
      proTip:
        "Simple offers are easier to sell and easier to deliver well.",
      bottomLine:
        "Start focused. Clear offers create cleaner growth."
    }
  },

  {
    id: "find-your-niche",
    title: "How to Choose a Niche Without Boxing Yourself In",
    category: "Start",
    summary: "Focus enough to market clearly without limiting your future growth.",
    tags: ["Beginner", "Workflow"],
    content: {
      overview:
        "A niche can make marketing easier, but it should not trap your business. The goal is to become known for solving a type of problem or serving a type of buyer without cutting off healthy opportunities.",
      whyItMatters:
        "Trying to sell to everyone usually results in weak messaging. On the other hand, picking a niche too narrowly can leave money on the table if demand is inconsistent.",
      steps: [
        "Look at what types of customers are easiest for you to understand and serve well.",
        "Choose a niche that already buys apparel or transfers regularly.",
        "Start by focusing your marketing on one or two main buyer groups.",
        "Use your messaging to highlight those groups without refusing work outside them.",
        "Track which customer types are most profitable and easiest to manage."
      ],
      mistakes: [
        "Trying to market to everyone at once.",
        "Choosing a niche only because it sounds trendy.",
        "Locking yourself into a market with weak local demand.",
        "Refusing good-fit work outside your first niche."
      ],
      proTip:
        "You do not need a forever niche. You need a clear starting point.",
      bottomLine:
        "Pick a niche that helps your message land, then adjust as real business data comes in."
    }
  },

  // ======================
  // PRICING
  // ======================

  {
    id: "pricing-dtf",
    title: "How to Price DTF Work",
    category: "Pricing",
    summary: "Stop guessing and build pricing that actually protects profit.",
    tags: ["Must Read", "Money"],
    content: {
      overview:
        "Pricing is one of the biggest differences between a business that stays busy and a business that survives well. Many shops work hard but still stay underpaid because they never calculate true cost.",
      whyItMatters:
        "Weak pricing makes volume dangerous. More orders can mean more stress and more work without solving the real problem of low margin.",
      steps: [
        "Calculate real production cost first, including film, ink, powder, waste, labor, packaging, and failed prints.",
        "Add overlooked overhead such as electricity, cleaning supplies, subscriptions, and maintenance.",
        "Set a profit goal before quoting the job.",
        "Use different logic for one-off jobs and volume work.",
        "Build risk and rush into your pricing mindset so difficult jobs do not quietly destroy margin."
      ],
      mistakes: [
        "Copying competitor pricing with no idea what their costs are.",
        "Ignoring spoilage and remakes.",
        "Quoting off instinct alone.",
        "Confusing being busy with being profitable."
      ],
      proTip:
        "If a job feels barely worth doing, your price is probably too low.",
      bottomLine:
        "Profit must be built into the quote from the start."
    }
  },

  {
    id: "gang-sheet-pricing",
    title: "How to Price Gang Sheets",
    category: "Pricing",
    summary: "Build a pricing model that respects layout efficiency, waste, and real production math.",
    tags: ["Money", "Advanced"],
    content: {
      overview:
        "Gang sheet pricing looks simple until layout efficiency and waste start affecting real margin. Large orders can be great money, but only if the pricing model reflects how usable space is actually consumed.",
      whyItMatters:
        "A badly priced gang sheet can turn big volume into weak profit. The bigger the order, the bigger the damage when the math is wrong.",
      steps: [
        "Choose a consistent pricing model such as by size, by usable space, by sheet tier, or by square area.",
        "Account for layout efficiency because not every gang sheet is packed equally well.",
        "Separate pricing logic for repeat wholesale-style buyers and occasional one-off customers.",
        "Track trimming, setup, tests, and remakes so your pricing reflects real production.",
        "Standardize your quoting process so you are not reinventing the math each time."
      ],
      mistakes: [
        "Using flat pricing without regard to used space.",
        "Treating all gang sheets as equally efficient.",
        "Ignoring waste and rework.",
        "Offering discounts before the numbers are proven."
      ],
      proTip:
        "Profit on gang sheets comes from efficient use of space more than volume alone.",
      bottomLine:
        "Bulk jobs only help when your pricing model respects workflow reality."
    }
  },

  {
    id: "custom-shirt-pricing",
    title: "How to Price Custom Shirts",
    category: "Pricing",
    summary: "Price finished garments like a service, not just a transfer.",
    tags: ["Money", "Beginner"],
    content: {
      overview:
        "Custom shirts involve more than print cost. You are selling a finished product, which means garment sourcing, pressing, alignment, folding, packaging, and customer handling all need to be covered.",
      whyItMatters:
        "Many shops undercharge custom shirts because they only think about the transfer cost and ignore the extra time and risk of delivering a finished item.",
      steps: [
        "Start with garment cost, including sourcing and shipping.",
        "Add pressing, prep, alignment, folding, and packing labor.",
        "Factor in remake risk because finished garments carry higher error pain.",
        "Price the convenience of the finished product, not just the physical materials.",
        "Make sure the final price still leaves room for customer service and profit."
      ],
      mistakes: [
        "Pricing a finished garment like a loose transfer.",
        "Ignoring labor time.",
        "Undervaluing convenience.",
        "Using identical markup on every garment type."
      ],
      proTip:
        "A shirt is not just a print. It is a complete delivered product.",
      bottomLine:
        "Price finished garments based on full value, not just transfer cost."
    }
  },

  {
    id: "hidden-costs",
    title: "Hidden Costs That Kill Profit",
    category: "Pricing",
    summary: "Find the silent costs that make busy shops still feel tight on money.",
    tags: ["Money", "Must Read"],
    content: {
      overview:
        "Some of the biggest profit killers are not obvious line items. They hide in wasted time, spoiled materials, maintenance, packaging, subscriptions, and all the little things that add up quietly.",
      whyItMatters:
        "If you only price off visible materials, your margin can look fine on paper while your business still feels like it is constantly squeezed.",
      steps: [
        "Track failed prints, test prints, spoilage, and trimming waste.",
        "Measure time honestly, including file prep, messages, packing, and cleanup.",
        "Account for cleaning supplies, replacement parts, and downtime.",
        "Include packaging, labels, software, transaction fees, and small recurring costs.",
        "Compare quotes to real month-end profitability instead of trusting estimate math alone."
      ],
      mistakes: [
        "Ignoring small recurring costs.",
        "Pretending your time has no value.",
        "Not counting waste because it feels inconsistent.",
        "Thinking material-only math tells the whole story."
      ],
      proTip:
        "If you are not tracking it, it may be costing you more than you think.",
      bottomLine:
        "True cost is everything the job consumes, not just what you buy for it."
    }
  },

  {
    id: "rush-pricing",
    title: "How to Charge for Rush Orders",
    category: "Pricing",
    summary: "Protect your schedule, stress level, and existing customers when urgent jobs show up.",
    tags: ["Money", "Workflow"],
    content: {
      overview:
        "Rush work is not just faster work. It interrupts the schedule, creates pressure, and often forces you to reshuffle other jobs already in progress.",
      whyItMatters:
        "If rush work is charged like normal work, your business absorbs the disruption while the customer gets the benefit.",
      steps: [
        "Define what counts as rush in your business, such as same day, next day, or inside your normal turnaround window.",
        "Create a clear rush fee structure instead of making it up each time.",
        "Explain why rush work costs more so customers understand the value.",
        "Only accept rush jobs when they fit capacity or are worth the disruption.",
        "Use rush pricing to protect existing jobs from being devalued."
      ],
      mistakes: [
        "Not charging extra because you want to keep everyone happy.",
        "Accepting every urgent request automatically.",
        "Letting rush jobs disrupt better-paying normal work.",
        "Training customers to expect emergency speed at regular pricing."
      ],
      proTip:
        "Rush should feel premium because it costs you something real.",
      bottomLine:
        "Charge for urgency or it will quietly damage your business."
    }
  },

  {
    id: "discounts-without-losing-profit",
    title: "How to Offer Discounts Without Killing Profit",
    category: "Pricing",
    summary: "Use discounts strategically instead of cutting price blindly.",
    tags: ["Money", "Advanced"],
    content: {
      overview:
        "Discounts can help win business, but careless discounting trains customers to expect lower pricing while shrinking your margin.",
      whyItMatters:
        "Once customers get used to reduced pricing, it becomes harder to bring prices back to healthy levels. Discounts should support strategy, not replace it.",
      steps: [
        "Set clear rules for when discounts apply, such as true volume, repeat business, or easy-to-produce jobs.",
        "Know your minimum acceptable margin before offering any reduction.",
        "Discount in exchange for something valuable, such as bigger quantity, simpler artwork, or longer turnaround flexibility.",
        "Use tiered pricing instead of emotional one-off deals.",
        "Track whether discounted customers become good repeat clients or just demand lower prices forever."
      ],
      mistakes: [
        "Discounting because you feel awkward saying no.",
        "Lowering price with no clear reason.",
        "Offering deep discounts to difficult customers.",
        "Confusing more sales with better business."
      ],
      proTip:
        "The best discount is one tied to a better business outcome for you too.",
      bottomLine:
        "Discount with purpose, not pressure."
    }
  },

  // ======================
  // MANAGEMENT
  // ======================

  {
    id: "order-management",
    title: "How to Manage Orders",
    category: "Management",
    summary: "Keep orders organized so details do not slip and mistakes do not multiply.",
    tags: ["Workflow", "Must Read"],
    content: {
      overview:
        "Order management is what keeps a DTF shop from turning into confusion. Every job should have clear details, clear status, and a clear next step.",
      whyItMatters:
        "Wrong sizes, wrong files, missed approvals, and late communication all cost real money. A clean system protects time, trust, and profit.",
      steps: [
        "Use one repeatable intake process for every order.",
        "Collect all job details before production starts, including size, quantity, file version, deadline, and approval.",
        "Move jobs through clear stages such as received, approved, in production, ready, and complete.",
        "Store notes in one place instead of relying on scattered conversations.",
        "Review order details before the job enters production."
      ],
      mistakes: [
        "Relying on memory instead of a system.",
        "Keeping details across random texts and DMs.",
        "Starting before approval is locked in.",
        "Having no status tracking."
      ],
      proTip:
        "A simple system you actually use is better than a fancy system you abandon.",
      bottomLine:
        "Good order management reduces preventable mistakes and scales much better."
    }
  },

  {
    id: "artwork-handling",
    title: "How to Handle Customer Artwork",
    category: "Management",
    summary: "Control the artwork process so bad files and version confusion do not become remakes.",
    tags: ["Workflow", "Beginner"],
    content: {
      overview:
        "Artwork handling is one of the easiest places for preventable problems to start. Bad files, revision confusion, and weak approval systems can turn a simple job into a costly remake.",
      whyItMatters:
        "Artwork problems often reveal themselves after production has already started, which means the customer still expects a correct result and you are the one absorbing the damage.",
      steps: [
        "Set clear file requirements before customers send artwork.",
        "Review quality, sizing, transparency, and print readiness before approval.",
        "Name files clearly so the approved version is obvious.",
        "Get artwork approval in writing before printing.",
        "Store approved files in a way that makes future repeats easy."
      ],
      mistakes: [
        "Printing low-quality files because the customer sounded confident.",
        "Using the wrong revision.",
        "Skipping final approval because everyone is in a hurry.",
        "Keeping files in a mess of random folders."
      ],
      proTip:
        "Approval saves money. Ambiguity costs it.",
      bottomLine:
        "Control the file process and you reduce one of the biggest sources of preventable remakes."
    }
  },

  {
    id: "turnaround-times",
    title: "How to Set Turnaround Times",
    category: "Management",
    summary: "Set timelines you can actually meet without living in emergency mode.",
    tags: ["Workflow", "Beginner"],
    content: {
      overview:
        "Turnaround time is part of your reputation. Realistic timelines create trust and protect your schedule from constant pressure.",
      whyItMatters:
        "Missed deadlines create stress and damage trust quickly. Consistent delivery builds confidence and makes the business feel more stable.",
      steps: [
        "Set turnaround based on real capacity, not what sounds competitive.",
        "Build buffer into normal timelines.",
        "Clarify whether turnaround begins after payment, approval, or garment arrival.",
        "Adjust lead times when order volume rises.",
        "Review your actual delivery performance and update your stated timeline if needed."
      ],
      mistakes: [
        "Overpromising to win the order.",
        "Using vague answers like probably tomorrow.",
        "Ignoring approval delays and supply issues.",
        "Treating every order like it deserves the same speed."
      ],
      proTip:
        "Underpromise and overdeliver is still one of the easiest ways to look professional.",
      bottomLine:
        "A realistic turnaround protects trust and keeps your shop healthier."
    }
  },

  {
    id: "reprints-and-mistakes",
    title: "How to Handle Reprints and Mistakes",
    category: "Management",
    summary: "Respond professionally when something goes wrong and use mistakes to strengthen the system.",
    tags: ["Workflow", "Advanced"],
    content: {
      overview:
        "Every production business makes mistakes. The difference is how quickly and professionally the issue is handled once it happens.",
      whyItMatters:
        "Customers often judge your business more by the recovery than by the mistake itself. A strong response can protect trust while a weak one can destroy it.",
      steps: [
        "Own clear shop-side errors quickly.",
        "Fix the issue efficiently without getting defensive.",
        "Figure out what caused the mistake so it can be reduced next time.",
        "Use the situation to strengthen checks, approvals, or production systems.",
        "Keep communication calm and clear throughout the recovery."
      ],
      mistakes: [
        "Blaming the customer too early.",
        "Waiting too long to respond.",
        "Treating repeated mistakes like random bad luck.",
        "Having no consistent process for reprints."
      ],
      proTip:
        "Fast, professional recovery usually earns more trust than excuses ever will.",
      bottomLine:
        "Mistakes cost money, but how you handle them can still protect the relationship and improve the business."
    }
  },

  {
    id: "customer-policies",
    title: "How to Write Customer Policies",
    category: "Management",
    summary: "Set clear rules that protect your time, money, and professionalism.",
    tags: ["Must Read", "Workflow"],
    content: {
      overview:
        "Customer policies are not there to make the business feel stiff. They are there to reduce confusion, define expectations, and protect both sides from preventable conflict.",
      whyItMatters:
        "Without policies, every disagreement becomes emotional and negotiable. With them, expectations are defined before problems show up.",
      steps: [
        "Write simple policies for payments, approvals, turnaround, and remake responsibility.",
        "Use plain language customers can understand quickly.",
        "Apply the policies consistently.",
        "Make sure your policies match how your business actually runs.",
        "Update the policies as your business grows."
      ],
      mistakes: [
        "Having no written policies at all.",
        "Using vague language.",
        "Making so many exceptions that the policy becomes meaningless.",
        "Waiting until a problem happens before mentioning the rules."
      ],
      proTip:
        "Clarity prevents more problems than charm does.",
      bottomLine:
        "Good policies protect your business without making you look difficult."
    }
  },

  {
    id: "customer-communication",
    title: "How to Communicate Like a Pro",
    category: "Management",
    summary: "Make customers feel guided, informed, and confident without overcomplicating communication.",
    tags: ["Workflow", "Beginner"],
    content: {
      overview:
        "Clear communication is part of the product. Customers want to feel like they know what is happening, what you need from them, and what to expect next.",
      whyItMatters:
        "Bad communication creates confusion, delays, and unnecessary tension. Good communication makes the entire business feel more professional and trustworthy.",
      steps: [
        "Respond clearly and directly instead of writing long, messy explanations.",
        "Tell customers what you need from them and by when.",
        "Use the same basic communication flow for quotes, approvals, updates, and pickup.",
        "Keep tone professional and calm, especially when a customer is stressed.",
        "Confirm important job details in writing."
      ],
      mistakes: [
        "Writing vague responses.",
        "Leaving customers guessing what happens next.",
        "Overexplaining when simple would work better.",
        "Failing to document key decisions."
      ],
      proTip:
        "The best communication feels simple, confident, and easy to follow.",
      bottomLine:
        "Strong communication reduces friction and makes your shop feel more trustworthy."
    }
  },

  // ======================
  // MARKETING
  // ======================

  {
    id: "first-customers",
    title: "How to Get First Customers",
    category: "Marketing",
    summary: "Get in front of real buyers and land your first paying jobs.",
    tags: ["Must Read", "Beginner"],
    content: {
      overview:
        "Your first customers usually do not come from perfect branding. They come from visibility, trust, proof of work, and direct effort.",
      whyItMatters:
        "Many new shops wait to be discovered. Early growth usually comes from active outreach and consistent visibility instead of passive hope.",
      steps: [
        "Start local first where trust is easier to build.",
        "Make it obvious what you sell and who it helps.",
        "Show real samples or real work, even if your portfolio is small.",
        "Reach out directly to local businesses, teams, event organizers, and small brands.",
        "Use a simple first offer that makes saying yes easier."
      ],
      mistakes: [
        "Waiting for customers to appear on their own.",
        "Trying to market to everybody.",
        "Having no proof of work.",
        "Failing to follow up."
      ],
      proTip:
        "Early sales come from clarity and action more than perfection.",
      bottomLine:
        "Be visible, be clear, and make the first decision easy."
    }
  },

  {
    id: "local-marketing",
    title: "How to Market a Local DTF Business",
    category: "Marketing",
    summary: "Use your local area as a real advantage instead of chasing random attention online.",
    tags: ["Beginner", "Money"],
    content: {
      overview:
        "Local marketing is one of the best advantages a small DTF shop has. Nearby buyers are often easier to trust, easier to serve, and more likely to become repeat customers.",
      whyItMatters:
        "Local clients can turn into steady work because they often need ongoing uniforms, event shirts, staff apparel, and repeat transfer jobs.",
      steps: [
        "Identify local buyer types that already need custom apparel or transfers.",
        "Prepare simple samples or photos that show relevant work clearly.",
        "Reach out directly in a professional, helpful way.",
        "Build relationships instead of treating local business like one-off transactions.",
        "Stay visible with follow-ups, local groups, social proof, and networking."
      ],
      mistakes: [
        "Ignoring local opportunities while chasing only online buyers.",
        "Showing up with no clear offer.",
        "Talking too much about your equipment instead of the outcome.",
        "Not following up."
      ],
      proTip:
        "Local trust compounds faster than random reach.",
      bottomLine:
        "A local DTF shop grows faster when people nearby know exactly what you do."
    }
  },

  {
    id: "facebook-marketplace",
    title: "Facebook Marketplace and Local Lead Tips",
    category: "Marketing",
    summary: "Use Facebook more like a lead machine and less like a random posting board.",
    tags: ["Beginner", "Money"],
    content: {
      overview:
        "Facebook can still generate real business when your offer is clear and your response time is strong. It works best when you treat it like active lead handling, not passive advertising.",
      whyItMatters:
        "Facebook puts you close to local demand. Many buyers there care more about convenience and trust than about polished branding.",
      steps: [
        "Use clear photos and short, direct descriptions.",
        "Test different offers such as transfers, event shirts, business apparel, or local custom orders.",
        "Respond quickly and clearly when leads message you.",
        "Refresh posts and listings consistently.",
        "Track which types of posts create real conversations and sales."
      ],
      mistakes: [
        "Posting vague listings with weak photos.",
        "Replying slowly.",
        "Using the same poor post repeatedly.",
        "Expecting one listing to do all the work."
      ],
      proTip:
        "Speed and clarity win more often than polish on Facebook.",
      bottomLine:
        "Consistent posting and fast replies can turn Facebook into a dependable lead source."
    }
  },

  {
    id: "reach-small-brands",
    title: "How to Reach Out to Small Brands",
    category: "Marketing",
    summary: "Position yourself as a reliable partner instead of just another print provider.",
    tags: ["Money", "Advanced"],
    content: {
      overview:
        "Small brands often need consistency, speed, and communication more than they need the absolute lowest price. If you make their life easier, you can become part of their repeat workflow.",
      whyItMatters:
        "Brand clients can produce repeat revenue and more stable work than random one-off orders.",
      steps: [
        "Find small brands that already sell apparel or need repeat merchandise support.",
        "Reach out with a short message focused on how you can help them.",
        "Show relevant examples of work they can relate to.",
        "Offer an easy first step such as a small test order or sample.",
        "Follow up professionally if they do not respond the first time."
      ],
      mistakes: [
        "Sending long, self-centered pitches.",
        "Talking only about yourself.",
        "Giving up after one message.",
        "Reaching out without proof of quality."
      ],
      proTip:
        "The best outreach feels easy to say yes to.",
      bottomLine:
        "Brand outreach works when you sound like a solution, not just a salesperson."
    }
  },

  {
    id: "repeat-business",
    title: "How to Build Repeat Business",
    category: "Marketing",
    summary: "Turn good customers into long-term clients with less effort than constant customer hunting.",
    tags: ["Must Read", "Money"],
    content: {
      overview:
        "Repeat business is one of the most valuable things a DTF shop can build. Existing customers are usually cheaper to keep than new customers are to acquire.",
      whyItMatters:
        "A business that depends only on constant new customer hunting stays fragile. Repeat business smooths out revenue and makes growth more stable.",
      steps: [
        "Deliver consistently so customers feel safe ordering again.",
        "Communicate clearly before, during, and after the order.",
        "Follow up after successful jobs.",
        "Store files, notes, and preferences so repeat orders feel easy.",
        "Reward loyalty when it makes sense through smoother service or priority handling."
      ],
      mistakes: [
        "Ignoring customers once the order is complete.",
        "Making repeat orders harder than needed.",
        "Being inconsistent from one order to the next.",
        "Putting all energy into acquisition only."
      ],
      proTip:
        "The easiest next sale is often from someone who already trusts you.",
      bottomLine:
        "Retention usually pays better than constant chasing."
    }
  },

  {
    id: "content-that-sells",
    title: "How to Create Content That Actually Helps Sales",
    category: "Marketing",
    summary: "Show the kind of work and proof that makes buyers trust you faster.",
    tags: ["Beginner", "Workflow"],
    content: {
      overview:
        "Good content does not just get likes. It helps buyers understand what you do, how it looks, and why they should trust you with an order.",
      whyItMatters:
        "Weak content makes good work easy to overlook. Strong content builds trust even before the conversation starts.",
      steps: [
        "Post clear photos and videos of real work instead of random filler.",
        "Show details buyers care about, such as garment feel, color, finish, and quality.",
        "Use captions that explain what the job was and who it was for.",
        "Mix finished product content with process content and proof-of-quality content.",
        "Stay consistent enough that people remember you."
      ],
      mistakes: [
        "Posting only random graphics with no context.",
        "Using poor lighting and weak photos.",
        "Focusing on your machine more than your customer result.",
        "Being too inconsistent to stay memorable."
      ],
      proTip:
        "Buyers trust proof more than hype.",
      bottomLine:
        "Content should make it easier for a customer to picture buying from you."
    }
  },

  // ======================
  // SCALING
  // ======================

  {
    id: "scale-without-chaos",
    title: "How to Scale Without Chaos",
    category: "Scaling",
    summary: "Grow demand without letting quality, sanity, and profit fall apart.",
    tags: ["Must Read", "Advanced"],
    content: {
      overview:
        "Scaling is not just getting more orders. It is being able to handle more work without breaking the system underneath it.",
      whyItMatters:
        "If your current workflow is sloppy, growth will multiply the damage instead of solving it.",
      steps: [
        "Tighten your current process before chasing more volume.",
        "Identify bottlenecks in design prep, printing, curing, pressing, packing, and communication.",
        "Batch similar work where possible.",
        "Standardize repeat tasks so less work depends on improvisation.",
        "Upgrade tools and workflow based on proven bottlenecks."
      ],
      mistakes: [
        "Scaling before fixing quality issues.",
        "Adding products too early.",
        "Letting volume outrun process control.",
        "Assuming more orders automatically means success."
      ],
      proTip:
        "Controlled growth beats impressive chaos.",
      bottomLine:
        "Real scaling comes from stronger systems, not just more sales."
    }
  },

  {
    id: "upgrade-equipment",
    title: "When to Upgrade Equipment",
    category: "Scaling",
    summary: "Upgrade because the business needs it, not because comparison pressure tells you to.",
    tags: ["Money", "Advanced"],
    content: {
      overview:
        "Upgrades should solve real problems. The right equipment move removes friction and improves return. The wrong one just creates new expenses.",
      whyItMatters:
        "Wrong upgrades can drain cash without improving profit. Right upgrades remove bottlenecks and help growth feel more sustainable.",
      steps: [
        "Identify the actual problem first, such as speed, consistency, capacity, or labor.",
        "Estimate the return from the upgrade in time saved, waste reduced, or capacity increased.",
        "Ask whether the issue is really equipment or still a workflow problem.",
        "Upgrade when profitable demand is being held back.",
        "Make sure your surrounding systems can support the new equipment."
      ],
      mistakes: [
        "Upgrading too early because bigger feels more serious.",
        "Buying based on hype.",
        "Ignoring payback math.",
        "Using equipment as a substitute for fixing process."
      ],
      proTip:
        "Good upgrades remove pain you can already measure.",
      bottomLine:
        "Upgrade to solve a proven business problem, not a feeling."
    }
  },

  {
    id: "hire-help",
    title: "When to Hire Help",
    category: "Scaling",
    summary: "Know when support will actually free capacity instead of creating more confusion.",
    tags: ["Advanced", "Workflow"],
    content: {
      overview:
        "At some point, the owner becomes the bottleneck. Hiring help is about deciding which tasks truly require you and which tasks should move to someone else.",
      whyItMatters:
        "If everything depends on you forever, growth stays capped no matter how much demand exists.",
      steps: [
        "Track where your time goes so you know what tasks drain capacity.",
        "Start by handing off simple, repeatable tasks.",
        "Document the process before delegating it.",
        "Hire based on actual workflow need instead of ego.",
        "Train clearly so help reduces pressure instead of adding errors."
      ],
      mistakes: [
        "Hiring too early with no clear role.",
        "Hiring too late and staying buried.",
        "Expecting people to fix weak systems automatically.",
        "Giving work away with no training."
      ],
      proTip:
        "The best first hire usually buys back your time.",
      bottomLine:
        "Hiring works best when the process is ready to be shared."
    }
  },

  {
    id: "batch-orders",
    title: "How to Batch Orders for Speed",
    category: "Scaling",
    summary: "Reduce wasted motion and mental switching by grouping compatible work together.",
    tags: ["Workflow", "Advanced"],
    content: {
      overview:
        "Batching is one of the easiest ways to improve efficiency. Instead of constantly jumping between unrelated tasks, you group similar work and move through it faster.",
      whyItMatters:
        "Task switching creates drag. Batching helps you stay in rhythm and reduces wasted setup effort.",
      steps: [
        "Group jobs by similar garment type, print type, or production stage.",
        "Run compatible prints in sequence.",
        "Press similar jobs together when possible.",
        "Create staging areas so batched work stays organized.",
        "Review whether your batches are reducing time or just creating piles."
      ],
      mistakes: [
        "Constantly switching between unrelated tasks.",
        "Trying to batch with no staging system.",
        "Letting batches become clutter.",
        "Ignoring urgency and priority completely."
      ],
      proTip:
        "Less switching usually means more speed and fewer mistakes.",
      bottomLine:
        "Batching works when the workflow is organized enough to support it."
    }
  },

  {
    id: "stop-being-bottleneck",
    title: "How to Stop Being the Bottleneck",
    category: "Scaling",
    summary: "Build systems so the business can move without every decision depending on you.",
    tags: ["Must Read", "Advanced"],
    content: {
      overview:
        "Many businesses stall because the owner is still the center of every task and decision. That works in survival mode, but it does not scale well.",
      whyItMatters:
        "If every quote, file check, production step, and customer response depends on you, the business can only move as fast as you personally can.",
      steps: [
        "Identify where your involvement is still required and why.",
        "Document repeatable processes so they do not live only in your head.",
        "Delegate lower-level decisions once the process is stable.",
        "Automate reminders, intake, file organization, and repeat communication where possible.",
        "Shift your role toward oversight and improvement instead of touching everything."
      ],
      mistakes: [
        "Doing everything out of habit.",
        "Keeping the business only in your head.",
        "Refusing to trust process or support.",
        "Confusing personal control with quality."
      ],
      proTip:
        "Systems scale better than effort alone.",
      bottomLine:
        "The business gets stronger when you stop being the choke point."
    }
  },

  {
    id: "build-simple-sops",
    title: "How to Build Simple SOPs for Your Shop",
    category: "Scaling",
    summary: "Create repeatable instructions so the business runs more consistently every day.",
    tags: ["Workflow", "Advanced"],
    content: {
      overview:
        "Standard operating procedures do not need to be corporate or complicated. Simple written steps for repeat tasks can dramatically reduce mistakes and make training easier.",
      whyItMatters:
        "Without SOPs, the business depends too much on memory and mood. With them, tasks are easier to repeat, teach, and improve.",
      steps: [
        "Start with the tasks that happen all the time, such as intake, file checks, printing, pressing, and packing.",
        "Write the steps simply enough that someone else could follow them.",
        "Include what good looks like and what to double-check.",
        "Test the SOP in real workflow and revise what is unclear.",
        "Keep SOPs short enough that people will actually use them."
      ],
      mistakes: [
        "Overcomplicating the process documents.",
        "Writing SOPs no one follows.",
        "Leaving critical checks out.",
        "Failing to update them when the workflow changes."
      ],
      proTip:
        "The best SOP is clear, short, and used often.",
      bottomLine:
        "Simple SOPs make the shop easier to run and easier to scale."
    }
  }
];